import React, { useEffect, useState } from 'react';
import { User, Mail, Coins, Calendar, ArrowUpRight, ArrowDownLeft, Loader2, CreditCard, RefreshCw } from 'lucide-react';
import { db } from '../firebase';
import { UserProfile, Transaction } from '../types';

interface ProfileProps {
  user: UserProfile;
}

export const Profile: React.FC<ProfileProps> = ({ user }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = () => {
    if (!user.uid) return;
    setLoading(true);
    
    // We removed .orderBy() to prevent index requirement errors.
    // Client-side sorting is fast enough for <100 items.
    const unsubscribe = db.collection('transactions')
      .where('userId', '==', user.uid)
      .limit(50)
      .onSnapshot((snapshot: any) => {
        const data: Transaction[] = snapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Client-side sort: Newest first
        data.sort((a, b) => {
            const timeA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
            const timeB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
            return timeB - timeA;
        });

        setTransactions(data);
        setLoading(false);
      }, (err: any) => {
        console.error("Failed to fetch transactions", err);
        setLoading(false);
      });

    return unsubscribe;
  };

  useEffect(() => {
    const unsubscribe = fetchTransactions();
    return () => { if(unsubscribe) unsubscribe(); };
  }, [user.uid]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-slate-900 h-32 relative">
           <div className="absolute inset-0 bg-[radial-gradient(#3b82f6_1px,transparent_1px)] [background-size:16px_16px] opacity-20"></div>
        </div>
        <div className="px-8 pb-8">
           <div className="relative -mt-12 mb-6 flex flex-col md:flex-row items-center md:items-start gap-6 justify-between">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6 text-center md:text-left">
                 <div className="w-24 h-24 rounded-full border-4 border-white bg-white shadow-md overflow-hidden shrink-0">
                    <img src={user.photoURL || 'https://via.placeholder.com/150'} alt="Profile" className="w-full h-full object-cover" />
                 </div>
                 {/* FIXED: Use explicit padding top (pt-16) to push name down into the white section, avoiding banner overlap */}
                 <div className="mb-1 pt-16 md:pt-14">
                    <h1 className="text-2xl font-bold text-slate-900">{user.displayName}</h1>
                    <div className="flex items-center justify-center md:justify-start gap-2 text-slate-500 text-sm">
                       <Mail size={14} /> {user.email}
                    </div>
                 </div>
              </div>
              
              <div className="bg-primary-50 px-6 py-3 rounded-xl border border-primary-100 flex items-center gap-4 mt-4 md:mt-12 w-full md:w-auto">
                 <div className="w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center shrink-0">
                    <Coins size={20} />
                 </div>
                 <div>
                    <p className="text-xs text-primary-600 font-bold uppercase tracking-wider">Available Credits</p>
                    <p className="text-2xl font-extrabold text-slate-900">{user.credits}</p>
                 </div>
              </div>
           </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
               <Calendar size={20} className="text-slate-400" /> Credit History
            </h2>
            <button 
                onClick={() => fetchTransactions()} 
                className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors"
                title="Refresh History"
            >
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
        </div>
        
        {loading ? (
            <div className="flex justify-center py-12">
               <Loader2 className="animate-spin text-slate-400" />
            </div>
        ) : transactions.length === 0 ? (
            <div className="text-center py-12 text-slate-400 bg-slate-50 rounded-lg border border-dashed border-slate-200">
               No transactions found yet. Run a tool to see history here.
            </div>
        ) : (
            <div className="overflow-x-auto">
               <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-slate-900 font-semibold border-b border-slate-200">
                     <tr>
                        <th className="px-4 py-3 rounded-tl-lg">Type</th>
                        <th className="px-4 py-3">Description</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3 text-right rounded-tr-lg">Credits</th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                     {transactions.map(tx => (
                        <tr key={tx.id} className="hover:bg-slate-50 transition-colors">
                           <td className="px-4 py-3">
                              {tx.type === 'purchase' ? (
                                 <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                                    <CreditCard size={12} /> Purchase
                                 </span>
                              ) : tx.type === 'refund' ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                                   <ArrowDownLeft size={12} /> Refund
                                </span>
                              ) : (
                                 <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-600">
                                    <ArrowUpRight size={12} /> Usage
                                 </span>
                              )}
                           </td>
                           <td className="px-4 py-3 font-medium text-slate-900">
                              {tx.description || 'Credit usage'}
                              {tx.amount ? <span className="ml-2 text-xs text-slate-400">(${tx.amount / 100})</span> : ''}
                           </td>
                           <td className="px-4 py-3 text-slate-500 font-mono text-xs">
                              {formatDate(tx.timestamp)}
                           </td>
                           <td className={`px-4 py-3 text-right font-bold font-mono ${tx.credits > 0 ? 'text-green-600' : 'text-slate-900'}`}>
                              {tx.credits > 0 ? '+' : ''}{tx.credits}
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
        )}
      </div>
    </div>
  );
};
